/*
 * Akva — valg af GLS-pakkeshop i kurven (snippets/akva-pakkeshop.liquid).
 *
 * Postnummer → GLS' offentlige pakkeshopsøgning → et klik vælger. Valget
 * gemmes som ekstra felter på kurven (/cart/update.js), så det følger med
 * ordren. Kan kunden ikke finde sin pakkeshop, eller svarer GLS ikke, kan
 * den skrives i hånden.
 *
 * "Til kassen" åbner vælgeren, så længe der ikke er valgt en pakkeshop.
 * Kurven bliver tegnet om (morph) efter hver ændring, men serveren tegner
 * valget ud fra kurvens felter, så det står der stadig bagefter.
 */

const GLS_URL = 'https://www.gls.dk/webservices_v4/wsShopFinder.asmx/SearchNearestParcelShops';
const GLS_NS = 'http://gls.dk/webservices/';
const ANTAL = 8;

// Navnene på felterne står også i snippettet og i pakkesedlen.
const FELT_NR = 'GLS-pakkeshop nr.';
const FELT_TEKST = 'GLS-pakkeshop';

class AkvaPakkeshop extends HTMLElement {
  /** @type {AbortController | null} */
  #soegning = null;

  connectedCallback() {
    // Alt går gennem delegering på elementet selv. Kurven tegnes om med
    // morph, som retter i de samme elementer i stedet for at skifte dem ud,
    // men lyttere på børnene ville alligevel være skrøbelige.
    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#soegning?.abort();
  }

  get valgt() {
    return this.hasAttribute('data-valgt');
  }

  get dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-akva-ps-dialog]'));
  }

  /** @param {string} name */
  #part(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-akva-ps-${name}]`));
  }

  /** @param {string} name */
  #text(name) {
    return this.dataset[`text${name[0].toUpperCase()}${name.slice(1)}`] || '';
  }

  /**
   * Åbner vælgeren. Fra "Til kassen" står der en linje øverst om, at der
   * skal vælges en pakkeshop først.
   * @param {{ krav?: boolean, detail?: number }} [options]
   */
  open({ krav = false, detail = 0 } = {}) {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;

    const kravLinje = this.#part('krav');
    if (kravLinje) {
      kravLinje.textContent = krav ? this.#text('required') : '';
      kravLinje.hidden = !krav;
    }

    dialog.showModal();

    // Som "Find a retailer": efter et tryk eller klik får modalen selv fokus
    // (ingen fokusring om krydset på iPhonen). Med tastaturet går fokus til
    // postnummerfeltet, så man kan skrive med det samme.
    const zip = /** @type {HTMLInputElement | null} */ (this.#part('zip'));
    if (detail > 0) dialog.focus({ preventScroll: true });
    else zip?.focus();
  }

  close() {
    this.dialog?.close();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const dialog = this.dialog;

    // Klik inde i modalen må ikke nå kurvskuffen. Skuffen lukker ved klik
    // uden for sin egen kasse, og modalen står midt på skærmen.
    if (dialog && dialog.contains(target)) event.stopPropagation();

    if (target.closest('[data-akva-ps-open]')) {
      this.open({ detail: event.detail });
      return;
    }

    if (target.closest('[data-akva-ps-close]') || target === dialog) {
      this.close();
      return;
    }

    if (target.closest('[data-akva-ps-manuel-open]')) {
      const form = this.#part('manuel');
      if (form) {
        form.hidden = false;
        /** @type {HTMLTextAreaElement | null} */ (this.#part('manuel-tekst'))?.focus();
      }
      return;
    }

    const shop = /** @type {HTMLButtonElement | null} */ (target.closest('.akva-ps__shop'));
    if (shop) {
      this.#gem({
        nr: shop.dataset.nr || '',
        tekst: shop.dataset.tekst || '',
      });
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    event.preventDefault();
    event.stopPropagation();

    if (form.matches('[data-akva-ps-soeg]')) {
      const zip = /** @type {HTMLInputElement | null} */ (this.#part('zip'));
      this.#soeg((zip?.value || '').trim());
    } else if (form.matches('[data-akva-ps-manuel]')) {
      const tekst = /** @type {HTMLTextAreaElement | null} */ (this.#part('manuel-tekst'));
      const vaerdi = (tekst?.value || '').trim().replace(/\s*\n\s*/g, ', ');
      if (!vaerdi) {
        tekst?.focus();
        return;
      }
      this.#gem({ nr: '', tekst: vaerdi });
    }
  };

  /** Esc lukker kun modalen, ikke kurvskuffen bag den. @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.dialog?.open) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }
  };

  /** @param {string} zip */
  async #soeg(zip) {
    const status = this.#part('status');
    const liste = this.#part('liste');
    if (!status || !liste) return;

    if (!/^\d{4}$/.test(zip)) {
      status.textContent = this.#text('invalid');
      liste.replaceChildren();
      return;
    }

    this.#soegning?.abort();
    const soegning = new AbortController();
    this.#soegning = soegning;

    status.textContent = this.#text('searching');
    liste.replaceChildren();

    try {
      const url = new URL(GLS_URL);
      url.search = new URLSearchParams({
        street: '',
        zipcode: zip,
        countryIso3166A2: 'DK',
        Amount: String(ANTAL),
      }).toString();

      const response = await fetch(url, { signal: soegning.signal });
      if (!response.ok) throw new Error(`GLS ${response.status}`);

      const xml = new DOMParser().parseFromString(await response.text(), 'text/xml');
      const shops = [...xml.getElementsByTagNameNS(GLS_NS, 'PakkeshopData')].map((node) => {
        /** @param {string} tag */
        const felt = (tag) => node.getElementsByTagNameNS(GLS_NS, tag)[0]?.textContent?.trim() || '';
        return {
          nr: felt('Number'),
          navn: felt('CompanyName'),
          gade: felt('Streetname'),
          postnr: felt('ZipCode'),
          by: felt('CityName'),
        };
      });

      if (soegning.signal.aborted) return;

      if (shops.length === 0) {
        status.textContent = this.#text('empty');
        return;
      }

      status.textContent = this.#text('found');
      this.#visListe(shops);
    } catch (error) {
      if (soegning.signal.aborted) return;
      // GLS svarer ikke: så er feltet til at skrive den selv vejen.
      status.textContent = this.#text('error');
      const form = this.#part('manuel');
      if (form) form.hidden = false;
    }
  }

  /** @param {{ nr: string, navn: string, gade: string, postnr: string, by: string }[]} shops */
  #visListe(shops) {
    const liste = this.#part('liste');
    if (!liste) return;

    const valgtNr = this.dataset.nr || '';
    const nrTekst = this.#text('number');

    liste.replaceChildren(
      ...shops.map((shop) => {
        const adresse = `${shop.gade}, ${shop.postnr} ${shop.by}`;
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'akva-ps__shop';
        button.dataset.nr = shop.nr;
        button.dataset.tekst = `${shop.navn}, ${adresse}`;
        button.setAttribute('aria-pressed', String(shop.nr === valgtNr));

        const navn = document.createElement('span');
        navn.className = 'akva-ps__shop-navn';
        navn.textContent = shop.navn;

        const nr = document.createElement('span');
        nr.className = 'akva-ps__nr';
        nr.textContent = ` · ${nrTekst} ${shop.nr}`;

        const linje = document.createElement('span');
        linje.className = 'akva-ps__shop-adresse';
        linje.append(adresse, nr);

        const tekst = document.createElement('span');
        tekst.className = 'akva-ps__shop-tekst';
        tekst.append(navn, linje);

        const flueben = document.createElement('span');
        flueben.className = 'akva-ps__shop-flueben';
        flueben.setAttribute('aria-hidden', 'true');
        flueben.textContent = '✓';

        button.append(tekst, flueben);
        li.append(button);
        return li;
      })
    );
  }

  /** @param {{ nr: string, tekst: string }} valg */
  async #gem({ nr, tekst }) {
    const status = this.#part('status');
    if (status) status.textContent = this.#text('saving');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // En tom værdi fjerner feltet, så et gammelt nummer ikke bliver hængende,
        // når pakkeshoppen er skrevet i hånden.
        body: JSON.stringify({ attributes: { [FELT_NR]: nr, [FELT_TEKST]: tekst } }),
      });
      if (!response.ok) throw new Error(`cart ${response.status}`);
    } catch (error) {
      if (status) status.textContent = this.#text('saveError');
      return;
    }

    if (status) status.textContent = '';
    this.#visValg(nr, tekst);
    this.close();
  }

  /**
   * Viser valget i kurven med det samme. Næste gang kurven tegnes om, kommer
   * den samme tekst fra serveren.
   * @param {string} nr
   * @param {string} tekst
   */
  #visValg(nr, tekst) {
    this.toggleAttribute('data-valgt', Boolean(tekst));
    this.dataset.nr = nr;

    const valg = this.#part('valg');
    if (valg) {
      valg.textContent = tekst;
      if (nr) {
        const nrSpan = document.createElement('span');
        nrSpan.className = 'akva-ps__nr';
        nrSpan.textContent = ` · ${this.#text('number')} ${nr}`;
        valg.append(nrSpan);
      }
    }

    const open = this.#part('open');
    if (open) open.textContent = this.#text('change');
  }
}

if (!customElements.get('akva-pakkeshop')) {
  customElements.define('akva-pakkeshop', AkvaPakkeshop);

  // "Til kassen" åbner vælgeren, indtil der er valgt en pakkeshop. Lytteren
  // står på dokumentet i capture-fasen, så den kommer før kassens egen
  // indsendelse — og den overlever, at kurven tegnes om.
  document.addEventListener(
    'click',
    (event) => {
      const knap = /** @type {Element} */ (event.target).closest?.('.cart__checkout-button');
      if (!knap) return;

      const ramme = knap.closest('.cart-drawer__summary, .cart-summary__inner') || document;
      const vaelger = /** @type {AkvaPakkeshop | null} */ (ramme.querySelector('akva-pakkeshop'));
      if (!vaelger || vaelger.valgt) return;

      event.preventDefault();
      event.stopPropagation();
      vaelger.open({ krav: true, detail: /** @type {MouseEvent} */ (event).detail });
    },
    true
  );
}
